import { styled } from 'styled-components';

import { ImagePreprocessingRow } from 'types';
import usePreprocessingCard from '../hooks/usePreprocessingCard';
import Select from 'components/ui/Select';
import Button from 'components/ui/Button';
import { IoClose } from 'react-icons/io5';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;

  &:not(:first-child) {
    padding-top: 20px;
  }

  &:not(:last-child) {
    padding-bottom: 20px;
    border-bottom: 1px solid ${(props) => props.theme.global.borderColor};
  }
`;

const FormView = styled.div``;

const TitleView = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  margin-bottom: 6px;
`;

const Title = styled.label``;

const PreviewButton = styled.button`
  color: ${(props) => props.theme.global.mainColor};
  font-weight: 500;
  &:hover {
    text-decoration: underline;
  }
`;

const SelectView = styled.div`
  display: flex;
  gap: 30px;
`;

const CardContainer = styled.div`
  overflow: auto;
  padding-bottom: 6px;
`;

const CardRow = styled.div`
  display: flex;
  gap: 20px;
`;

const Card = styled.div`
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  min-width: 160px;
  border: 1px solid ${(props) => props.theme.global.borderColor};
  border-radius: 8px;
  padding: 10px 14px;
  padding-top: 6px;
  background-color: ${(props) => props.theme.editor.flowBgColor};
  min-height: 120px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  button {
    position: relative;
    left: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 20px;
    padding: 4px;
    color: ${(props) => props.theme.global.lightTextColor};
  }
`;

const CardContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-top: 10px;

  & > div {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;

    label {
      font-size: 13px;
    }
  }
`;

const ParamInput = styled.input<{ $error: boolean }>`
  width: 70px;
  border: 1px solid ${(props) => (props.$error ? props.theme.global.errorColor : props.theme.global.borderColor)};
  border-radius: ${(props) => (props.$error ? '4px' : 0)};
  outline: none;
  color: ${(props) => props.theme.global.textColor};
  padding: 4px 10px;
  font-size: 12px;
  text-align: end;
  background-color: ${(props) => props.theme.editor.blockNode.inputBgColor};
`;

const PreprocessingRow = (props: ImagePreprocessingRow) => {
  const {
    title,
    name,
    functions,
    useFormReturn: {
      formState: { errors },
      register,
    },
    nodeDataSave,
  } = props;
  const { preprocessings, onAddClick, onDeleteClick, onPreviewClick } = usePreprocessingCard({ ...props });

  return (
    <Container>
      <FormView>
        <TitleView>
          <Title>{title}</Title>
          <PreviewButton onClick={onPreviewClick}>미리보기</PreviewButton>
        </TitleView>
        <SelectView>
          <Select maxWidth={340} {...register(`${name}Select`)}>
            {Object.keys(functions).map((name) => (
              <option key={`${name}Select_${name}`}>{name}</option>
            ))}
          </Select>
          <Button $width={130} onClick={onAddClick}>
            추가하기
          </Button>
        </SelectView>
      </FormView>
      {preprocessings.length > 0 && (
        <CardContainer>
          <CardRow>
            {preprocessings.map((func, i) => (
              <Card key={`${name}_${func.name}_${i}`}>
                <CardHeader>
                  <span>{func.name}</span>
                  <button onClick={(event) => onDeleteClick(event, i)}>
                    <IoClose />
                  </button>
                </CardHeader>
                <CardContent>
                  {func.parameters.map((parameter, j) => {
                    const isOptional = parameter.defaultValue === '_Optional';
                    const isBoolean = typeof parameter.defaultValue === 'boolean';

                    const isError = Boolean(errors[`${name}Preprocessings`]?.[i]?.parameters?.[j]?.value);

                    return (
                      <div key={`${name}_${func.name}_${i}_${parameter.param}`}>
                        <label>{parameter.param}</label>
                        {isBoolean ? (
                          <ParamInput
                            as="select"
                            {...register(`${name}Preprocessings.${i}.parameters.${j}.value`, {
                              onChange: nodeDataSave,
                            })}
                            $error={isError}
                          >
                            <option value="True">True</option>
                            <option value="False">False</option>
                          </ParamInput>
                        ) : (
                          <ParamInput
                            {...register(`${name}Preprocessings.${i}.parameters.${j}.value`, {
                              required: !isOptional,
                              onBlur: nodeDataSave,
                            })}
                            $error={isError}
                            placeholder={isOptional ? 'optional' : 'required'}
                          />
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ))}
          </CardRow>
        </CardContainer>
      )}
    </Container>
  );
};

export default PreprocessingRow;
